import React, { useEffect, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import MainCard from "../../utils/ui-components/MainCard";
import EmptyResultDataGrid from "../../utils/ui-components/EmptyResultDataGrid";
import CircularProgress from "@mui/material/CircularProgress";
import { gridSpacing } from "../../store/constants";
import DatePicker from "../../utils/ui-components/FormsUI/DatePicker";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { useTheme } from "@mui/material/styles";
import XLSX from "sheetjs-style";
import * as Yup from "yup";
import DownloadIcon from "@mui/icons-material/Download";
import { ReportService } from "../../assets/_services/report-service";
import { useDispatch, useSelector } from "react-redux";
import { getState, MENU_OPEN, SET_BREADCRUMBS } from "../../redux/actions/actions";
import FilterUser from "./filter-user";
import { currencyFormat } from "../../utils/utils";
import { useLocation } from "react-router-dom";

const INITIAL_FORM_STATE = {
  fromDate: dayjs().subtract(30, "day"),
  toDate: dayjs(),
};

const FORM_VALIDATION = Yup.object().shape({
  fromDate: Yup.date().nullable().required("Please Select a Date").typeError("please enter a valid date"),
  toDate: Yup.date().nullable().required("Please Select a Date").typeError("please enter a valid date"),
});

function DataFinancialReportChannelWise() {
  const theme: any = useTheme();
  const authState = useSelector((state: any) => state.auth.authData);
  const user = getState(authState);

  const [reportData, setReportData]: any = useState([]);
  const [search, setSearch] = useState(INITIAL_FORM_STATE);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [count, setCount] = useState(0);
  const [userId, setUserId] = useState("");

  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const path = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const id = path?.pathname;
    dispatch({ type: MENU_OPEN, id });
    window.history.replaceState({}, "");
    dispatch({
      type: SET_BREADCRUMBS,
      payload: [
        { name: "Home", path: "/Dashboard", bold: true },
        { name: "Reward Financial Report", path: null, bold: false },
      ],
    });
  }, []);

  const fetchData = (searchValue: any) => {
    setIsLoading(true);
    let formData = {
      fromDate: searchValue?.fromDate?.format("YYYY-MM-DD"),
      toDate: searchValue?.toDate?.format("YYYY-MM-DD"),
      userId: userId,
    };
    ReportService.getDataFinanceReportDataChannelWise(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
      if (response.isSuccess) {
        setReportData(response.data.data.rows);
        setCount(response.data.data.count);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData(search);
  }, [paginationModel]);

  const getDownload = (searchObject: any) => {
    setIsDownloadLoading(true);
    let formData = {
      fromDate: searchObject?.fromDate?.format("YYYY-MM-DD"),
      toDate: searchObject?.toDate?.format("YYYY-MM-DD"),
    };
    ReportService.getDataFinanceReportDataChannelWise(formData, 0, count).then((response) => {
      if (response.isSuccess) {
        let downloadData = response.data.data.rows.map((item: any) => {
          return {
            Operator: item?.operatorName,
            Channel: item?.channelName,
            Date: item?.date,
            "Request count": item?.channel_request_count,
            "Success count": item?.channel_request_success_count,
            "Fail count": item?.channel_request_failed_count,
            "Operator data rate": item?.operator_data_pack_rate,
            "Platform data rate": item?.platform_data_pack_rate,
            // "Total operator price": item?.total_operator_price,
            // "Platform chargeable price": item?.platform_chargeable_price,
            // "Platform chargeable price with Tax": item?.platform_chargeable_price_with_tax,
            // "Total Price": item?.total_price,
          };
        });
        exportToExcel(downloadData);
        setIsDownloadLoading(false);
      } else {
        setIsDownloadLoading(false);
      }
    });
  };

  const exportToExcel = async (data: any[]) => {
    // const fileExcel = '.xlsx';
    const fileCsv = ".csv";
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    // XLSX.writeFile(wb, 'campaign_report' + fileExcel);
    XLSX.writeFile(wb, "reward_financial_report" + fileCsv);
  };

  const columns = [
    {
      field: "operatorName",
      filterable: true,
      headerName: "Operator Name",
      sortable: true,
      disableExport: false,
      minWidth: 130,
      flex: 1,
    },
    {
      field: "channelName",
      filterable: true,
      headerName: "Channel Name",
      sortable: true,
      disableExport: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "date",
      filterable: true,
      headerName: "Date",
      sortable: true,
      disableExport: false,
      minWidth: 120,
      flex: 1,
      renderCell: (params: any) => {
        let date = dayjs(params.row.date).format("YYYY-MM-DD");
        return <div style={{ textAlign: "center", width: "100%", paddingRight: 10 }}>{date}</div>;
      },
    },
    {
      field: "channel_request_count",
      filterable: true,
      headerName: "Request Count",
      sortable: true,
      disableExport: false,
      minWidth: 150,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
            {currencyFormat(Number(params?.row?.channel_request_count), "", 0)}
          </div>
        );
      },
    },
    {
      field: "channel_request_success_count",
      filterable: true,
      headerName: "Success Count",
      sortable: true,
      disableExport: false,
      minWidth: 150,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
            {currencyFormat(Number(params.row.channel_request_success_count), "", 0)}
          </div>
        );
      },
    },
    {
      field: "channel_request_failed_count",
      filterable: true,
      headerName: "Fail Count",
      sortable: true,
      disableExport: false,
      minWidth: 130,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
            {currencyFormat(Number(params.row.channel_request_failed_count), "", 0)}
          </div>
        );
      },
    },
    {
      field: "operator_data_pack_rate",
      filterable: true,
      headerName: "Operator Data Pack Rate",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 3,
      renderCell: (params: any) => {
        return (
          <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
            {currencyFormat(Number(params.row?.operator_data_pack_rate), "", 2)}
          </div>
        );
      },
    },
    {
      field: "platform_data_pack_rate",
      filterable: true,
      headerName: "Platform Data Pack Rate",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 3,
      renderCell: (params: any) => {
        return (
          <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
            {currencyFormat(Number(params.row?.platform_data_pack_rate), "", 2)}
          </div>
        );
      },
    },
    // {
    //   field: "total_operator_price",
    //   filterable: true,
    //   headerName: "Total Operator Price",
    //   sortable: false,
    //   disableExport: false,
    //   minWidth: 220,
    //   flex: 3,
    //   renderCell: (params: any) => {
    //     return (
    //       <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>{currencyFormat(Number(params.row?.total_operator_price), "", 2)}</div>
    //     );
    //   },
    // },
    // {
    //   field: "platform_chargeable_price",
    //   filterable: true,
    //   headerName: "Platform Chargeable Price",
    //   sortable: false,
    //   disableExport: false,
    //   minWidth: 220,
    //   flex: 3,
    //   renderCell: (params: any) => {
    //     return (
    //       <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
    //         {currencyFormat(Number(params.row?.platform_chargeable_price), "", 2)}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   field: "platform_chargeable_price_with_tax",
    //   filterable: true,
    //   headerName: "Platform Chargeable Price with Tax",
    //   sortable: false,
    //   disableExport: false,
    //   minWidth: 250,
    //   flex: 3,
    //   renderCell: (params: any) => {
    //     return (
    //       <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>
    //         {currencyFormat(Number(params.row?.platform_chargeable_price_with_tax), "", 2)}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   field: "total_price",
    //   filterable: true,
    //   headerName: "Total Price",
    //   sortable: false,
    //   disableExport: false,
    //   minWidth: 220,
    //   flex: 3,
    //   renderCell: (params: any) => {
    //     return <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>{currencyFormat(Number(params.row?.total_price), "", 2)}</div>;
    //   },
    // },
  ];

  return (
    <Grid container justifyContent={"center"} spacing={gridSpacing}>
      <Grid item xl={12} lg={12} xs={12}>
        <MainCard title="Reward Financial Report">
          <Formik
            initialValues={{
              ...INITIAL_FORM_STATE,
            }}
            validationSchema={FORM_VALIDATION}
            onSubmit={(values: any, { setSubmitting }) => {
              fetchData(values);
              setSearch(values);
            }}
          >
            {({ values, dirty, isSubmitting }) => (
              <Form>
                <Grid container columnSpacing={gridSpacing}>
                  <Grid item lg={2.5} md={3} sm={5} xs={12}>
                    <DatePicker name="fromDate" label="From Date" placeholder="From Date" maxDate={values.toDate || dayjs()} />
                  </Grid>
                  <Grid item lg={2.5} md={3} sm={5} xs={12}>
                    <DatePicker name="toDate" label="To Date" placeholder="To Date" maxDate={dayjs()} minDate={values.fromDate} />
                  </Grid>
                  {(user?.role === "2" || user?.role === "1" || user?.role === "4") && (
                    <Grid item lg={3.5} md={4} sm={6} xs={12}>
                      <FilterUser setUserId={setUserId} />
                    </Grid>
                  )}
                  <Grid item lg={3.5} md={5} sm={6} xs={12}>
                    <Button color="primary" variant="contained" type="submit" sx={{ ...theme.typography.customInput, mr: 1 }} disabled={isLoading}>
                      View
                    </Button>
                    {(user?.role === "2" || user?.role === "3" ? user?.isReportDownload === 1 : true) && (
                      <Button
                        color="primary"
                        variant="contained"
                        sx={{ ...theme.typography.customInput }}
                        type="button"
                        disabled={isDownloadLoading || reportData.length === 0}
                        startIcon={isDownloadLoading ? <CircularProgress color="inherit" sx={{ mr: 1 }} size={20} /> : <DownloadIcon />}
                        onClick={() => {
                          getDownload(search);
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
          <Box sx={{ minHeight: 400, width: "100%", mt: 1 }}>
            <DataGrid
              autoHeight
              columns={columns}
              loading={isLoading}
              rows={reportData}
              slots={{
                noRowsOverlay: EmptyResultDataGrid,
                loadingOverlay: () => <LinearProgress />,
              }}
              style={{ overflow: "auto" }}
              pagination
              getRowId={(row) => row.id}
              paginationMode="server"
              rowCount={count}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
            {/* <Button variant="contained" disabled={isExportingExcel}
                                endIcon={isExportingExcel ? <CircularProgress color="inherit" size={20}/> : <GetAppIcon/>}
                                onClick={exportTOExcel} style={{marginTop: 5}}>
                            Export Excel
                        </Button> */}
          </Box>
        </MainCard>
      </Grid>
    </Grid>
  );
}

export default DataFinancialReportChannelWise;
